import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { apiBase, getTokens } from './api';
import { Toaster } from './components/Toasts';
import { bootstrapLang } from './i18n';
import { type QueuedWrite, startOutbox } from './offline';
import { queryClient } from './query';
import { router } from './router';
import { applyTheme, watchSystemTheme } from './theme';

// Self-hosted, not the Google Fonts CDN: this is an installed PWA that people
// open in a basement car park, and a heading should not wait on a third party
// to have an opinion. Weights are the ones the design actually uses.
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/plus-jakarta-sans/600.css';
import '@fontsource/plus-jakarta-sans/700.css';
import '@fontsource/plus-jakarta-sans/800.css';
import '@fontsource/jetbrains-mono/400.css';

// Icon set, bundled — see scripts/build-icons.mjs. Imported before anything
// renders so no icon is ever looked up over the network.
import './icons.generated';

import './styles.css';

bootstrapLang();

// PWA service worker (cache shell for offline + faster boot)
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      /* ignore — PWA is opt-in, not load-bearing */
    });
  });
}

const root = createRoot(document.getElementById('root')!);
root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster />
    </QueryClientProvider>
  </StrictMode>,
);

// Replay anything that was written with no signal. Straight fetch rather than
// api(): a replay must not re-enter the queue it came from, and a 401 here is
// for flush() to judge, not a reason to bounce somebody to the login screen
// while their phone is in their pocket.
applyTheme();
watchSystemTheme();

startOutbox(async (w: QueuedWrite) => {
  const tokens = getTokens();
  const res = await fetch(`${apiBase()}${w.path}`, {
    method: w.method,
    headers: {
      ...(w.body ? { 'content-type': 'application/json' } : {}),
      ...(tokens ? { authorization: `Bearer ${tokens.access_token}` } : {}),
    },
    ...(w.body ? { body: w.body } : {}),
  });
  return { ok: res.ok, status: res.status };
});
